import { GAS_BUFFER, TRANSACTION_EVENTS_MAP } from '../constants/transactions.js';
import { createEmitter, getRevertReason } from '../utils/transactions.js';
export default class TransactionsService {
    sdk;
    constructor(sdk) {
        this.sdk = sdk;
    }
    // Copied over from: https://github.com/Synthetixio/js-monorepo
    hash(transactionHash, chainId) {
        const emitter = createEmitter();
        setTimeout(() => this.watchTransaction(transactionHash, chainId, emitter), 5);
        return emitter;
    }
    async watchTransaction(hash, chainId, emitter) {
        emitter.emit(TRANSACTION_EVENTS_MAP.txSent, { transactionHash: hash });
        try {
            const client = this.sdk.context.clients[chainId] ?? this.sdk.context.signerPublicClient;
            const { status, blockNumber } = await client.waitForTransactionReceipt({
                hash,
            });
            if (status === 'success') {
                emitter.emit(TRANSACTION_EVENTS_MAP.txConfirmed, {
                    status,
                    blockNumber,
                    transactionHash: hash,
                });
            }
        }
        catch (_err) {
            try {
                const tx = await this.sdk.context.signerPublicClient.getTransaction({ hash });
                const revertReason = await getRevertReason({
                    txHash: hash,
                    networkId: tx.chainId,
                    blockNumber: tx.blockNumber,
                    client: this.sdk.context.signerPublicClient,
                });
                emitter.emit(TRANSACTION_EVENTS_MAP.txFailed, {
                    transactionHash: tx.hash,
                    failureReason: revertReason,
                });
            }
            catch (_e) {
                emitter.emit(TRANSACTION_EVENTS_MAP.txFailed, {
                    transactionHash: hash,
                    failureReason: 'Transaction reverted for an unknown reason',
                });
            }
        }
    }
    async estimateGas(request, targetChain) {
        if (targetChain && targetChain !== this.sdk.context.signerChainId) {
            await this.switchToTargetChain(targetChain);
        }
        const { maxFeePerGas, maxPriorityFeePerGas } = await this.sdk.context
            .publicClient(targetChain ?? this.sdk.context.signerChainId)
            .estimateFeesPerGas({
            ...request,
            type: 'eip1559',
        });
        // Add a buffer to the gas estimate
        return {
            maxFeePerGas: (maxFeePerGas * BigInt(GAS_BUFFER)) / 100n,
            maxPriorityFeePerGas,
        };
    }
    async switchToTargetChain(targetChain) {
        /**
         * Switches blockchain network and delays the execution to ensure the switch.
         * This solution is inspired by a workaround for a known issue in the wagmi library.
         * Reference: https://github.com/wevm/wagmi/issues/1565
         */
        await this.sdk.context.walletClient.switchChain({ id: targetChain });
        await new Promise((resolve) => setTimeout(resolve, 1000));
    }
    async signTypedData(params, targetChain) {
        if (targetChain && targetChain !== this.sdk.context.signerChainId) {
            await this.switchToTargetChain(targetChain);
        }
        return this.sdk.context.walletClient.signTypedData(params);
    }
    async signMessage(message, targetChain) {
        if (targetChain && targetChain !== this.sdk.context.signerChainId) {
            await this.switchToTargetChain(targetChain);
        }
        return this.sdk.context.walletClient.signMessage({
            message,
        });
    }
    async writeContract(params, targetChain) {
        if (targetChain && targetChain !== this.sdk.context.signerChainId) {
            await this.switchToTargetChain(targetChain);
        }
        const walletClient = this.sdk.context.walletClient; // Directly access the wallet client here.
        return walletClient.writeContract(params);
    }
    async sendTransaction(transaction, targetChain) {
        if (targetChain && targetChain !== this.sdk.context.signerChainId) {
            await this.switchToTargetChain(targetChain);
        }
        const walletClient = this.sdk.context.walletClient; // Directly access the wallet client here.
        return walletClient.sendTransaction(transaction);
    }
}
//# sourceMappingURL=transactions.js.map